import { memo, useMemo } from 'react';

import { Collapse, Typography } from 'antd';
import { PiDotOutlineFill, PiLifebuoyFill } from 'react-icons/pi';
import { RiUserSearchFill } from 'react-icons/ri';
import { TbWorldSearch } from 'react-icons/tb';

import { useTheme } from '../../../../context/ThemeProvider';
import { RouteTitle } from '../../../../shared/constants';
import { useAppSelector } from '../../../../shared/hooks';
import { checkTQConnectAccess } from '../../../../shared/tqConnectUtils';
import { dynamicItemTitle } from '../../../../shared/utils';
import { IChannel } from '../../../../types/communityTypes';
import {
  COMMUNITY_ROUTES,
  COMMUNITY_ROUTES_KEYS,
  ROUTES,
} from '../../../../types/routes';

const ICON_SIZE = 26;

interface Props {
  userAccessType: 'creator' | 'customer';
  handleSelectSection: (
    section: string,
    channelData:
      | IChannel
      | {
          name: string;
        },
    url: string,
    isChannelSelected?: boolean,
  ) => void;
}

const TQConnectMenuItems: React.FC<Props> = ({
  userAccessType,
  handleSelectSection,
}) => {
  const { colors: COLORS } = useTheme();

  const { activeSection } = useAppSelector((state) => state.community);

  const { isTQConnectEnabled } = checkTQConnectAccess();

  const baseMenuTitle = `${dynamicItemTitle('tq-offering', 'title')} Requests`;

  const itemsToShow = useMemo(() => {
    const list = [
      {
        key: COMMUNITY_ROUTES_KEYS[
          COMMUNITY_ROUTES.TQ_CONNECT_SERVICE_REQUESTS_DISCOVER
        ] as RouteTitle,
        title: userAccessType === 'creator' ? baseMenuTitle : 'Discover',
        icon: (
          size: number = ICON_SIZE,
          className?: string,
          onClick?: () => void,
        ) =>
          userAccessType === 'creator' ? (
            <PiLifebuoyFill
              size={size}
              className={className}
              onClick={onClick}
            />
          ) : (
            <TbWorldSearch
              size={size}
              className={className}
              onClick={onClick}
            />
          ),
        onClick: () => {
          handleSelectSection(
            COMMUNITY_ROUTES_KEYS[
              COMMUNITY_ROUTES.TQ_CONNECT_SERVICE_REQUESTS_DISCOVER
            ],
            { name: userAccessType === 'creator' ? baseMenuTitle : 'Discover' },
            ROUTES.TQ_CONNECT_SERVICE_REQUESTS_DISCOVER,
          );
        },
      },
      {
        key: COMMUNITY_ROUTES_KEYS[
          COMMUNITY_ROUTES.TQ_CONNECT_SERVICE_REQUESTS_MY_ACTIVITY
        ] as RouteTitle,
        title: 'My Activity',
        icon: (
          size: number = ICON_SIZE,
          className?: string,
          onClick?: () => void,
        ) => (
          <RiUserSearchFill
            size={size}
            className={className}
            onClick={onClick}
          />
        ),
        onClick: () => {
          handleSelectSection(
            COMMUNITY_ROUTES_KEYS[
              COMMUNITY_ROUTES.TQ_CONNECT_SERVICE_REQUESTS_MY_ACTIVITY
            ],
            { name: 'My Activity' },
            ROUTES.TQ_CONNECT_SERVICE_REQUESTS_MY_ACTIVITY,
          );
        },
      },
    ];

    // if user is customer, show all items both discover and my activity
    if (userAccessType === 'customer') {
      return list;
    }

    // else show only discover as only customers have access to my activity
    return list.filter(
      (item) =>
        item.key !==
        COMMUNITY_ROUTES_KEYS[
          COMMUNITY_ROUTES.TQ_CONNECT_SERVICE_REQUESTS_MY_ACTIVITY
        ],
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userAccessType, baseMenuTitle]);

  if (!isTQConnectEnabled || itemsToShow.length === 0) {
    return null;
  }

  if (itemsToShow.length === 1) {
    const item = itemsToShow[0];

    return (
      <button
        className={`community-drawer_static-list_button ${
          activeSection === item.key
            ? 'community-drawer_static-list_active-section'
            : ''
        }`}
        key={item.key}
        onClick={item.onClick}>
        <div className="community-drawer_static-list_button_wrapper">
          <div className="community-drawer_static-list_button_wrapper_svg-wrapper">
            {item.icon(ICON_SIZE)}
          </div>
          {item.title}
        </div>
      </button>
    );
  }

  return (
    <Collapse
      expandIcon={() => (
        <div
          className="expand-icon"
          style={{
            marginRight: 6,
          }}>
          <PiLifebuoyFill size={ICON_SIZE} color={COLORS.ICON} />
        </div>
      )}
      defaultActiveKey="tq-connect-menu-items"
      collapsible="disabled">
      <Collapse.Panel
        key="tq-connect-menu-items"
        header={
          <Typography.Title level={5} style={{ fontWeight: 400 }}>
            {baseMenuTitle}
          </Typography.Title>
        }>
        {itemsToShow.map((item) => {
          const isActive = item.key === activeSection;

          return (
            <button
              key={`tq-connect-menu-item-${item.key}`}
              className={`community-drawer_static-list_button ${
                isActive ? 'community-drawer_static-list_active-section' : ''
              }`}
              onClick={item.onClick}>
              <div className="community-drawer_static-list_button_wrapper">
                <div className="community-drawer_static-list_button_wrapper_svg-wrapper">
                  <PiDotOutlineFill size={ICON_SIZE} />
                </div>
                {item.title}
              </div>
            </button>
          );
        })}
      </Collapse.Panel>
    </Collapse>
  );
};

export default memo(TQConnectMenuItems);
